/**
 * A document's history, read out of its own change graph (RFC 0313 3b).
 *
 * HISTORY IS A PROJECTION, NOT A STORE. Every row here is computed from the
 * Automerge changes the document already carries and the note each one was
 * stamped with (3a). Nothing is written beside the document, so two surfaces
 * asking the same question of the same heads get the same answer, which is
 * why an undo from the side column shows as undone on the thread's card.
 *
 * A ROW IS A RUN, NOT A CHANGE. A person typing makes one change per
 * keystroke, so a row per change would be forty rows for one sentence.
 * Consecutive changes by the same person, close together in time and carrying
 * nothing but their name, are one row. An agent's change is always its own row:
 * each one is a deliberate edit with its own note and its own undo.
 *
 * IT TAKES A BOUND. Each row costs two `A.view`s, so the projection walks back
 * from the heads and stops at `limit` rows, which is what a newest-first column
 * wants, and the caller asks for more with `before`.
 */

import { Automerge as A } from '@automerge/automerge-repo/slim'
import {
  type ChangeNote,
  parseChangeMessage,
  region,
} from '../session/agent-edits'
import { readDocumentContent } from './reads'

/** One field of an app record that a row changed (3b). */
export type FieldChange =
  | {
      kind: 'value'
      /** Dotted, from inside `data`: `status`, `owner.name`. */
      path: string
      before?: unknown
      after?: unknown
    }
  | {
      kind: 'list'
      path: string
      /** Each added or removed item, named by its title field when it has one. */
      added: string[]
      removed: string[]
      /**
       * The Automerge object id of each added item, beside `added`, or null for
       * a plain value. Undo finds the item by this, never by its name: two
       * items can share a title (0313 adversarial review, finding 2).
       */
      addedIds?: (string | null)[]
    }

/** One row of history: a run of changes by one author, or one agent change. */
export interface HistoryChange {
  /** The run's last change. A row is named by it. */
  hash: string
  /** Every change in the run, oldest first. */
  hashes: string[]
  /** The heads the run started from: the content before it. */
  deps: string[]
  /** Seconds since the epoch, of the first and last change. */
  startTime: number
  time: number
  sessionId?: string
  /** The prompt or comment an agent's change answered. */
  turnId?: string
  authorId?: string
  note?: string
  reverts?: string
  restores?: string[]
  merges?: string
  /** What the run did to `content`, when it touched it. */
  text?: {
    removed: string
    added: string
    contextBefore: string
    contextAfter: string
  }
  /** What the run did to `data`, when it touched it. */
  fields?: FieldChange[]
  /** An undo naming a change in this run exists. */
  reverted: boolean
}

export interface ChangesPage {
  /** Newest first. */
  changes: HistoryChange[]
  /** Older history exists past the last row: ask again with its `hash`. */
  more: boolean
}

/** Two changes this far apart (seconds) are two rows, even from one person. */
const RUN_GAP = 5 * 60

/** Rows per page when the caller does not say. */
const DEFAULT_LIMIT = 50

interface Decoded {
  hash: string
  deps: string[]
  time: number
  note: ChangeNote | null
}

/** Decoding is O(changes); memoised per document object, which is per heads. */
const decodedCache = new WeakMap<object, Decoded[]>()

function decodeAll(doc: object): Decoded[] {
  const hit = decodedCache.get(doc)
  if (hit) return hit
  const out: Decoded[] = []
  for (const raw of A.getAllChanges(doc as A.Doc<unknown>)) {
    const c = A.decodeChange(raw)
    if (!c.hash) continue
    out.push({
      hash: c.hash,
      deps: c.deps,
      time: c.time,
      note: parseChangeMessage(c.message),
    })
  }
  decodedCache.set(doc, out)
  return out
}

/** A change that joins a neighbour's row: a name and nothing else. */
function joinable(note: ChangeNote | null): boolean {
  if (!note) return true
  return (
    !note.sessionId &&
    !note.note &&
    !note.reverts &&
    !note.restores &&
    !note.merges
  )
}

function sameAuthor(a: ChangeNote | null, b: ChangeNote | null): boolean {
  return (a?.authorId ?? null) === (b?.authorId ?? null)
}

/**
 * The newest `limit` rows of a document's history, walking back from its heads
 * (or from just before `before`, a row's `hash`, for the next page).
 */
export function projectChanges(
  doc: unknown,
  opts: { limit?: number; before?: string } = {},
): ChangesPage {
  if (!doc || typeof doc !== 'object') return { changes: [], more: false }
  let decoded: Decoded[]
  try {
    decoded = decodeAll(doc)
  } catch {
    return { changes: [], more: false }
  }
  const limit = opts.limit ?? DEFAULT_LIMIT

  const reverted = new Set<string>()
  for (const c of decoded) if (c.note?.reverts) reverted.add(c.note.reverts)

  let end = decoded.length - 1
  if (opts.before) {
    const at = decoded.findIndex((c) => c.hash === opts.before)
    if (at < 0) return { changes: [], more: false }
    // `before` names a row by its LAST change; the row itself began earlier,
    // so step back over the rest of its run before taking the next row.
    end = runStart(decoded, at) - 1
  }

  const changes: HistoryChange[] = []
  while (end >= 0 && changes.length < limit) {
    const start = runStart(decoded, end)
    const row = describe(doc, decoded.slice(start, end + 1), reverted)
    if (row) changes.push(row)
    end = start - 1
  }
  return { changes, more: end >= 0 }
}

/**
 * Every row one session wrote to this document, newest first (RFC 0313 3h).
 * Only that session's rows are described, so a document a person typed a
 * thousand changes into costs one decode, not a thousand views.
 */
export function sessionDocumentChanges(
  doc: unknown,
  sessionId: string,
): HistoryChange[] {
  if (!doc || typeof doc !== 'object') return []
  let decoded: Decoded[]
  try {
    decoded = decodeAll(doc)
  } catch {
    return []
  }
  const reverted = new Set<string>()
  for (const c of decoded) if (c.note?.reverts) reverted.add(c.note.reverts)
  const out: HistoryChange[] = []
  for (let i = decoded.length - 1; i >= 0; i--) {
    const c = decoded[i]
    if (c?.note?.sessionId !== sessionId) continue
    const row = describe(doc, [c], reverted)
    if (row) out.push(row)
  }
  return out
}

/**
 * The row a change belongs to, wherever it is in history: the run that ENDS at
 * `hash`. Null when this copy does not have the change, or the run changed
 * nothing a row shows.
 *
 * Never past `hash`. A row is named by its last change when it is drawn; if
 * the same person kept typing after that, the run has grown, and an undo of
 * the row someone looked at must not take the newer typing with it (0313
 * adversarial review, finding 3).
 */
export function changeRow(doc: unknown, hash: string): HistoryChange | null {
  if (!doc || typeof doc !== 'object') return null
  let decoded: Decoded[]
  try {
    decoded = decodeAll(doc)
  } catch {
    return null
  }
  const at = decoded.findIndex((c) => c.hash === hash)
  if (at < 0) return null
  const start = runStart(decoded, at)
  const reverted = new Set<string>()
  for (const c of decoded) if (c.note?.reverts) reverted.add(c.note.reverts)
  return describe(doc, decoded.slice(start, at + 1), reverted)
}

/** The first index of the run that ends at `end`. */
function runStart(decoded: Decoded[], end: number): number {
  const last = decoded[end]
  if (!last || !joinable(last.note)) return end
  let start = end
  while (start > 0) {
    const prev = decoded[start - 1]
    const cur = decoded[start]
    if (!prev || !cur) break
    if (!joinable(prev.note) || !sameAuthor(prev.note, last.note)) break
    if (cur.time - prev.time > RUN_GAP) break
    start--
  }
  return start
}

function describe(
  doc: object,
  run: Decoded[],
  reverted: Set<string>,
): HistoryChange | null {
  const first = run[0]
  const last = run[run.length - 1]
  if (!first || !last) return null
  let before: unknown
  let after: unknown
  try {
    before = A.view(doc as A.Doc<unknown>, first.deps)
    after = A.view(doc as A.Doc<unknown>, [last.hash])
  } catch {
    return null
  }
  const beforeText = readDocumentContent(before)
  const afterText = readDocumentContent(after)
  const text =
    beforeText === afterText
      ? undefined
      : (({ removed, added, contextBefore, contextAfter }) => ({
          removed,
          added,
          contextBefore,
          contextAfter,
        }))(region(beforeText, afterText))
  const fields = withItemIds(
    diffData(plain(before, 'data'), plain(after, 'data')),
    before,
    after,
  )
  // A run that changed neither (a timestamp, a title) is not a row.
  if (!text && !fields.length) return null
  const note = last.note ?? {}
  return {
    hash: last.hash,
    hashes: run.map((c) => c.hash),
    deps: first.deps,
    startTime: first.time,
    time: last.time,
    ...(note.sessionId ? { sessionId: note.sessionId } : {}),
    ...(note.turnId ? { turnId: note.turnId } : {}),
    ...(note.authorId ? { authorId: note.authorId } : {}),
    ...(note.note ? { note: note.note } : {}),
    ...(note.reverts ? { reverts: note.reverts } : {}),
    ...(note.restores ? { restores: note.restores } : {}),
    ...(note.merges ? { merges: note.merges } : {}),
    ...(text ? { text } : {}),
    ...(fields.length ? { fields } : {}),
    reverted: run.some((c) => reverted.has(c.hash)),
  }
}

/**
 * Give each list field the object ids of the items it added, read from the
 * document as it was right after the run. The added values are the list's
 * delta between before and after; each is matched to an object in the after
 * list by value, in order, so two equal additions take two different objects.
 */
function withItemIds(
  fields: FieldChange[],
  before: unknown,
  after: unknown,
): FieldChange[] {
  return fields.map((f) => {
    if (f.kind !== 'list' || !f.added.length) return f
    const items = listAt(after, f.path)
    if (!items) return f
    const prior = (listAt(before, f.path) ?? []).map(plainItem)
    const { added } = listDelta(prior, items.map(plainItem))
    const taken = new Set<number>()
    const addedIds = added.map((value) => {
      const want = JSON.stringify(value)
      const i = items.findIndex(
        (item, at) =>
          !taken.has(at) && JSON.stringify(plainItem(item)) === want,
      )
      if (i < 0) return null
      taken.add(i)
      const item = items[i]
      if (!item || typeof item !== 'object') return null
      try {
        return A.getObjectId(item as A.Doc<unknown>) ?? null
      } catch {
        return null
      }
    })
    return { ...f, addedIds }
  })
}

/** The list at a dotted path inside a document view's `data`, if it is one. */
function listAt(view: unknown, path: string): unknown[] | null {
  let cur: unknown = (view as { data?: unknown } | null)?.data
  for (const k of path ? path.split('.') : []) {
    cur = (cur as Record<string, unknown> | null)?.[k]
  }
  return Array.isArray(cur) ? (cur as unknown[]) : null
}

const plainItem = (v: unknown): unknown => {
  try {
    return JSON.parse(JSON.stringify(v))
  } catch {
    return v
  }
}

/** A field of a document view as plain JSON: Automerge text becomes a string. */
function plain(view: unknown, key: string): unknown {
  const v = (view as Record<string, unknown> | null)?.[key]
  if (v === undefined) return undefined
  try {
    return JSON.parse(JSON.stringify(v))
  } catch {
    return undefined
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v)
}

/**
 * What changed between two `data` values, as field paths (3b). A list says
 * which items came and went rather than which index moved, because an index is
 * not something a person can recognise in a row.
 */
export function diffData(before: unknown, after: unknown): FieldChange[] {
  const out: FieldChange[] = []
  walk('', before, after, out)
  // By path, so the same change reads the same whichever key Automerge lists first.
  return out.sort((x, y) => (x.path < y.path ? -1 : x.path > y.path ? 1 : 0))
}

function walk(path: string, a: unknown, b: unknown, out: FieldChange[]) {
  if (same(a, b)) return
  if (Array.isArray(a) && Array.isArray(b)) {
    const { added, removed } = listDelta(a, b)
    out.push({
      kind: 'list',
      path,
      added: added.map(label),
      removed: removed.map(label),
    })
    return
  }
  if (isRecord(a) && isRecord(b)) {
    for (const k of new Set([...Object.keys(a), ...Object.keys(b)])) {
      walk(path ? `${path}.${k}` : k, a[k], b[k], out)
    }
    return
  }
  out.push({
    kind: 'value',
    path,
    ...(a !== undefined ? { before: a } : {}),
    ...(b !== undefined ? { after: b } : {}),
  })
}

function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

/** Items in `b` not in `a`, and in `a` not in `b`, as multisets. */
function listDelta(a: unknown[], b: unknown[]) {
  const left = a.map((x) => JSON.stringify(x))
  const right = b.map((x) => JSON.stringify(x))
  const removed: unknown[] = []
  const pool = [...right]
  for (let i = 0; i < left.length; i++) {
    const at = pool.indexOf(left[i] as string)
    if (at >= 0) pool.splice(at, 1)
    else removed.push(a[i])
  }
  const added: unknown[] = []
  const pool2 = [...left]
  for (let i = 0; i < right.length; i++) {
    const at = pool2.indexOf(right[i] as string)
    if (at >= 0) pool2.splice(at, 1)
    else added.push(b[i])
  }
  return { added, removed }
}

/** How a list item is named in a row: its title field, or a short excerpt. */
export function label(item: unknown): string {
  if (isRecord(item)) {
    for (const k of ['title', 'name', 'text', 'label', 'id']) {
      const v = item[k]
      if (typeof v === 'string' && v) return v
    }
  }
  const s = typeof item === 'string' ? item : JSON.stringify(item)
  return s.length > 60 ? `${s.slice(0, 57)}...` : s
}
